<template>
  <div>
    <Head title="Transacciones de Caja" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <button
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <h1 class="text-3xl font-bold">Transacciones de Caja</h1>
      </div>

      <!-- Actions -->
      <Link
        href="/cash-transactions/create"
        class="btn-blue flex items-center gap-2"
      >
        <i class="fa fa-plus"></i>
        <span>Nueva Transacción</span>
      </Link>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-sm mb-6 p-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Caja</label>
          <select
            v-model="filters.cash_register_id"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          >
            <option value="">Todas las cajas</option>
            <option
              v-for="register in cashRegisters"
              :key="register.id"
              :value="register.id"
            >
              {{ register.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
          <select
            v-model="filters.type"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          >
            <option value="">Todos los tipos</option>
            <option value="opening">Apertura</option>
            <option value="sale">Venta</option>
            <option value="payment">Pago</option>
            <option value="withdrawal">Retiro</option>
            <option value="deposit">Depósito</option>
            <option value="transfer_in">Transferencia Entrante</option>
            <option value="transfer_out">Transferencia Saliente</option>
            <option value="closure">Cierre</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Fecha Desde</label>
          <input
            v-model="filters.date_from"
            type="date"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Fecha Hasta</label>
          <input
            v-model="filters.date_to"
            type="date"
            class="w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500"
          />
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="bg-white rounded-lg shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-center bg-gray-50">
            <th class="p-3 font-semibold">Fecha</th>
            <th class="p-3 font-semibold">Caja</th>
            <th class="p-3 font-semibold">Tipo</th>
            <th class="p-3 font-semibold">Monto</th>
            <th class="p-3 font-semibold">Descripción</th>
            <th class="p-3 font-semibold">Responsable</th>
            <th class="p-3 font-semibold">Acciones</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="transaction in transactions.data"
            :key="transaction.id"
            class="border-t border-gray-100 hover:bg-gray-50 transition-colors text-center"
          >
            <td class="p-3 text-gray-600">
              {{ formatDate(transaction.created_at) }}
            </td>

            <td class="p-3 text-gray-600">
              {{ transaction.cash_register?.name }}
            </td>

            <td class="p-3">
              <span
                :class="getTransactionTypeClass(transaction.type)"
                class="px-2 py-1 rounded-full text-xs font-medium"
              >
                {{ getTransactionTypeText(transaction.type) }}
              </span>
            </td>

            <td class="p-3">
              <div class="text-right">
                <p class="font-medium">{{ formatCurrency(transaction.amount) }}</p>
                <p class="text-xs text-gray-500">{{ formatCurrency(transaction.amount_bs, 'VES') }}</p>
                <p class="text-xs text-gray-500">{{ formatCurrency(transaction.amount_cop, 'COP') }}</p>
              </div>
            </td>

            <td class="p-3 text-gray-600">
              {{ transaction.description }}
            </td>

            <td class="p-3 text-gray-600">
              {{ transaction.responsible?.name }}
            </td>

            <td class="p-3">
              <div class="flex items-center justify-center space-x-2">
                <button
                  @click="showTransaction(transaction)"
                  class="text-blue-600 hover:text-blue-900"
                  title="Ver detalles"
                >
                  <i class="fa fa-eye"></i>
                </button>

                <!-- Only allow editing manual transactions -->
                <button
                  v-if="canEditTransaction(transaction)"
                  @click="editTransaction(transaction)"
                  class="text-yellow-600 hover:text-yellow-900"
                  title="Editar"
                >
                  <i class="fa fa-edit"></i>
                </button>

                <!-- Only allow deleting manual transactions -->
                <button
                  v-if="canDeleteTransaction(transaction)"
                  @click="deleteTransaction(transaction)"
                  class="text-red-600 hover:text-red-900"
                  title="Eliminar"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="!transactions.data || transactions.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="7">No hay transacciones.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      v-if="transactions?.links || transactions?.meta?.links"
      :links="transactions.links || transactions.meta.links"
      class="mt-6"
    />

    <!-- Transaction Details Modal -->
    <div
      v-if="selectedTransaction"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="selectedTransaction = null"
    >
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Detalles de Transacción</h3>

          <div class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700">Tipo</label>
              <span
                :class="getTransactionTypeClass(selectedTransaction.type)"
                class="px-2 py-1 rounded-full text-xs font-medium mt-1 inline-block"
              >
                {{ getTransactionTypeText(selectedTransaction.type) }}
              </span>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Caja</label>
              <p class="text-sm text-gray-900">{{ selectedTransaction.cash_register?.name }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Monto</label>
              <div class="text-sm">
                <p><strong>USD:</strong> {{ formatCurrency(selectedTransaction.amount) }}</p>
                <p><strong>BS:</strong> {{ formatCurrency(selectedTransaction.amount_bs, 'VES') }}</p>
                <p><strong>COP:</strong> {{ formatCurrency(selectedTransaction.amount_cop, 'COP') }}</p>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Descripción</label>
              <p class="text-sm text-gray-900">{{ selectedTransaction.description }}</p>
            </div>

            <div v-if="selectedTransaction.reference">
              <label class="block text-sm font-medium text-gray-700">Referencia</label>
              <p class="text-sm text-gray-900">{{ selectedTransaction.reference }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Responsable</label>
              <p class="text-sm text-gray-900">{{ selectedTransaction.responsible?.name }}</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700">Fecha</label>
              <p class="text-sm text-gray-900">{{ formatDate(selectedTransaction.created_at) }}</p>
            </div>
          </div>

          <div class="flex justify-end mt-6">
            <button
              @click="selectedTransaction = null"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'CashTransactionsIndex',

  components: {
    Head,
    Link,
    Pagination,
  },

  layout: Layout,

  props: {
    transactions: {
      type: Object,
      required: true
    },
    cashRegisters: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      selectedTransaction: null,
      filters: {
        cash_register_id: '',
        type: '',
        date_from: '',
        date_to: '',
      },
    }
  },

  watch: {
    filters: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/cash-transactions',
          pickBy(this.filters),
          { preserveState: true }
        )
      }, 300),
    },
  },

  methods: {
    back() {
      this.$inertia.visit('/')
    },

    getTransactionTypeClass(type) {
      const classes = {
        'opening': 'bg-blue-100 text-blue-800',
        'sale': 'bg-green-100 text-green-800',
        'payment': 'bg-green-100 text-green-800',
        'withdrawal': 'bg-red-100 text-red-800',
        'deposit': 'bg-blue-100 text-blue-800',
        'transfer_in': 'bg-purple-100 text-purple-800',
        'transfer_out': 'bg-purple-100 text-purple-800',
        'closure': 'bg-gray-100 text-gray-800',
      }
      return classes[type] || 'bg-gray-100 text-gray-800'
    },

    getTransactionTypeText(type) {
      const texts = {
        'opening': 'Apertura',
        'sale': 'Venta',
        'payment': 'Pago',
        'withdrawal': 'Retiro',
        'deposit': 'Depósito',
        'transfer_in': 'Transferencia Entrante',
        'transfer_out': 'Transferencia Saliente',
        'closure': 'Cierre',
      }
      return texts[type] || type
    },

    formatCurrency(value, currency = 'USD') {
      const currencies = {
        'USD': 'en-US',
        'VES': 'es-VE',
        'COP': 'es-CO'
      }

      const symbols = {
        'USD': 'USD',
        'VES': 'VES',
        'COP': 'COP'
      }

      return new Intl.NumberFormat(currencies[currency] || 'en-US', {
        style: 'currency',
        currency: symbols[currency] || 'USD'
      }).format(value || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('es-VE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    canEditTransaction(transaction) {
      // Only allow editing manual transactions (not automatic ones)
      return ['withdrawal', 'deposit', 'transfer_out'].includes(transaction.type)
    },

    canDeleteTransaction(transaction) {
      // Only allow deleting manual transactions (not automatic ones)
      return ['withdrawal', 'deposit', 'transfer_out'].includes(transaction.type)
    },

    showTransaction(transaction) {
      this.selectedTransaction = transaction
    },

    editTransaction(transaction) {
      // Navigate to edit page or open modal
      this.$inertia.visit(`/cash-transactions/${transaction.id}/edit`)
    },

    deleteTransaction(transaction) {
      if (confirm('¿Estás seguro de que quieres eliminar esta transacción?')) {
        this.$inertia.delete(`/cash-transactions/${transaction.id}`)
      }
    },
  }
}
</script>